import { apiFetch } from './client';

export interface MemoryEntry {
  key: string;
  value: string;
  agent_id: string;
  tags: string[];
  metadata: Record<string, unknown>;
  stated_importance: number;
  access_count: number;
  pinned: boolean;
  archived: boolean;
  created_ms: number;
  updated_ms: number;
  last_accessed_ms: number;
}

export interface MemoryStats {
  total_entries: number;
  short_term_count: number;
  long_term_count: number;
  archived_count: number;
  pinned_count: number;
  total_value_bytes?: number;
}

export function fetchMemoryEntries(
  agentId: string,
  opts: { tag?: string; limit?: number } = {},
): Promise<MemoryEntry[]> {
  const params = new URLSearchParams();
  if (opts.tag) params.set('tag', opts.tag);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return apiFetch<MemoryEntry[]>(
    `/api/memory/${agentId}${qs ? `?${qs}` : ''}`,
  );
}

export function searchMemory(
  agentId: string,
  query: string,
  limit = 50,
): Promise<MemoryEntry[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  return apiFetch<MemoryEntry[]>(
    `/api/memory/${agentId}/search?${params.toString()}`,
  );
}

export function fetchMemoryStats(agentId: string): Promise<MemoryStats> {
  return apiFetch<MemoryStats>(`/api/memory/${agentId}/stats`);
}

/** The URL path for one entry.
 *
 *  The key is encoded whole, as one path segment: a `/` in it becomes
 *  `%2F`, which the server decodes back into the key, so nothing in a key
 *  can act as a path separator. Keys are written by the agent, which reads
 *  untrusted content, and an earlier version that kept the `/`s would
 *  have let a key like `../../promotions/x/approve` turn the owner's
 *  click on Archive into a request to another route. A key that is
 *  exactly `.` or `..` is still read by the browser as a dot segment, so
 *  it is refused rather than sent. */
function entryPath(agentId: string, key: string): string {
  if (key === '.' || key === '..') {
    throw new Error(`A memory key of "${key}" cannot be addressed from the panel.`);
  }
  return `/api/memory/${encodeURIComponent(agentId)}/${encodeURIComponent(key)}`;
}

export interface MemoryEdit {
  value: string;
  tags?: string[];
  stated_importance?: number;
  pinned?: boolean;
}

/** Write an entry as the owner: stored `source="user"`, always in its
 *  history. Fields left out keep their current values. */
export function updateMemoryEntry(
  agentId: string,
  key: string,
  edit: MemoryEdit,
): Promise<MemoryEntry> {
  return apiFetch<MemoryEntry>(entryPath(agentId, key), {
    method: 'PUT',
    body: JSON.stringify(edit),
  });
}

/** Take an entry out of search and the agent's context, keeping its value
 *  for audit. */
export function archiveMemoryEntry(
  agentId: string,
  key: string,
): Promise<{ key: string; archived: boolean }> {
  return apiFetch(entryPath(agentId, key), {
    method: 'DELETE',
  });
}

/** Erase an entry and the earlier values its history holds. Admin only,
 *  and cannot be undone. */
export function purgeMemoryEntry(
  agentId: string,
  key: string,
): Promise<{ key: string; purged: boolean }> {
  return apiFetch(`${entryPath(agentId, key)}?purge=true`, {
    method: 'DELETE',
  });
}
